import { useEffect, useState } from "react";
import { apiUrl, currentUserId, currentUserQuery } from "./auth";
import { Modal } from "./Modal";
import { MultiChoiceField } from "./pickers";

export type OutsidePerson = { id: number; name: string; society: string };
export type OutsideReading = {
  id: number;
  kind: "fiche" | "week";
  owner_name: string;
  society: string;
  title: string;
  properties: { label: string; value: string }[];
};

function outsideUrl(extra = ""): string {
  const query = currentUserQuery();
  if (!extra) return apiUrl("outside.php") + query;
  const joiner = query ? "&" : "?";
  return apiUrl("outside.php") + query + joiner + extra;
}

function headers(): HeadersInit {
  const id = currentUserId();
  return { ...(id ? { "X-User-Id": id } : {}), "Content-Type": "application/json" };
}

async function read(res: Response): Promise<Record<string, unknown>> {
  try {
    return (await res.json()) as Record<string, unknown>;
  } catch {
    return {};
  }
}

export async function outsidePeople(): Promise<OutsidePerson[]> {
  const res = await fetch(outsideUrl("scope=people"), {
    credentials: "include",
    cache: "no-store",
    headers: headers(),
  });
  const data = await read(res);
  if (!res.ok || data.success === false || !Array.isArray(data.people)) return [];
  return data.people.flatMap((row) => {
    const item = row as { id?: number; name?: string; society?: string };
    const id = Number(item.id) || 0;
    if (id <= 0) return [];
    return [{ id, name: String(item.name || "").trim() || `Compte ${id}`, society: String(item.society || "").trim() }];
  });
}

export async function outsideSent(kind: "fiche" | "week", ref: number | string): Promise<number[]> {
  const query = kind === "fiche"
    ? `&scope=sent&kind=fiche&fiche_id=${encodeURIComponent(String(ref))}`
    : `&scope=sent&kind=week&week_task_id=${encodeURIComponent(String(ref))}`;
  const res = await fetch(outsideUrl(query.replace(/^&/, "")), {
    credentials: "include",
    cache: "no-store",
    headers: headers(),
  });
  const data = await read(res);
  if (!res.ok || data.success === false || !Array.isArray(data.viewers)) {
    throw new Error(String(data.message || "Lecture indisponible"));
  }
  return data.viewers.map((id) => Number(id) || 0).filter((id) => id > 0);
}

export async function outsideSet(kind: "fiche" | "week", ref: number | string, viewers: number[]): Promise<number[]> {
  const res = await fetch(outsideUrl(), {
    method: "POST",
    credentials: "include",
    cache: "no-store",
    headers: headers(),
    body: JSON.stringify(kind === "fiche"
      ? { action: "set", kind, fiche_id: Number(ref) || 0, viewers }
      : { action: "set", kind, week_task_id: String(ref), viewers }),
  });
  const data = await read(res);
  if (!res.ok || data.success === false) throw new Error(String(data.message || "Lecture impossible"));
  return Array.isArray(data.viewers) ? data.viewers.map((id) => Number(id) || 0).filter((id) => id > 0) : viewers;
}

async function outsideIncoming(): Promise<OutsideReading[]> {
  const res = await fetch(outsideUrl(), {
    credentials: "include",
    cache: "no-store",
    headers: headers(),
  });
  const data = await read(res);
  if (!res.ok || data.success === false || !Array.isArray(data.incoming)) return [];
  return data.incoming.flatMap((row) => {
    const item = row as OutsideReading;
    const id = Number(item.id) || 0;
    if (id <= 0) return [];
    const kind = item.kind === "week" ? "week" : "fiche";
    const properties = Array.isArray(item.properties)
      ? item.properties.flatMap((prop) => {
          const label = String(prop?.label || "").trim();
          const value = String(prop?.value || "").trim();
          return label && value ? [{ label, value }] : [];
        })
      : [];
    return [{
      id,
      kind,
      owner_name: String(item.owner_name || "").trim(),
      society: String(item.society || "").trim(),
      title: String(item.title || "").trim() || (kind === "week" ? "Tâche" : "Fiche"),
      properties,
    }];
  });
}

async function outsideLeave(id: number): Promise<void> {
  const res = await fetch(outsideUrl(), {
    method: "POST",
    credentials: "include",
    cache: "no-store",
    headers: headers(),
    body: JSON.stringify({ action: "leave", id }),
  });
  const data = await read(res);
  if (!res.ok || data.success === false) throw new Error(String(data.message || "Retrait impossible"));
}

function personLabel(person: OutsidePerson): string {
  return person.society ? `${person.name} · ${person.society}` : person.name;
}

export function OutsideShareField({
  kind,
  ficheId = 0,
  taskId = "",
  ids,
  onChange,
  persist = false,
  compact = false,
}: {
  kind: "fiche" | "week";
  ficheId?: number;
  taskId?: string;
  ids: number[];
  onChange: (ids: number[]) => void;
  persist?: boolean;
  compact?: boolean;
}) {
  const [people, setPeople] = useState<OutsidePerson[]>([]);
  const [note, setNote] = useState("");
  const live = kind === "fiche" ? ficheId > 0 : taskId !== "";

  useEffect(() => {
    let alive = true;
    void outsidePeople().then((rows) => {
      if (alive) setPeople(rows);
    });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (!persist || !live) return;
    let alive = true;
    void outsideSent(kind, kind === "fiche" ? ficheId : taskId).then((viewers) => {
      if (alive) onChange(viewers);
    }).catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [persist, live, kind, ficheId, taskId]);

  if (people.length === 0) return null;

  async function apply(values: string[]) {
    const next = values.map((value) => Number(value) || 0).filter((id) => id > 0);
    onChange(next);
    if (!persist || !live) return;
    setNote("");
    try {
      const saved = await outsideSet(kind, kind === "fiche" ? ficheId : taskId, next);
      onChange(saved);
    } catch (reason: unknown) {
      setNote(reason instanceof Error ? reason.message : "Lecture impossible");
    }
  }

  return (
    <div className={compact ? "w-36 shrink-0" : "flex min-w-0 flex-col items-stretch gap-1"} title={compact ? "Aperçu seulement. Le dossier du Coffre ne part pas." : undefined}>
      <p className={compact ? "mb-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400" : "text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400"}>Autre société</p>
      <MultiChoiceField
        values={ids.map(String)}
        options={people.map((person) => ({ value: String(person.id), label: personLabel(person) }))}
        emptyLabel="Personne"
        onChange={(values) => void apply(values)}
      />
      {compact ? null : <p className="text-xs text-slate-500">Aperçu seulement. Le dossier du Coffre ne part pas.</p>}
      {note ? <p className="text-xs text-rose-700">{note}</p> : null}
    </div>
  );
}

export function ReceivedStrip() {
  const [rows, setRows] = useState<OutsideReading[] | null>(null);
  const [open, setOpen] = useState<OutsideReading | null>(null);
  const [note, setNote] = useState("");

  useEffect(() => {
    let alive = true;
    void outsideIncoming().then((list) => {
      if (alive) setRows(list);
    });
    return () => {
      alive = false;
    };
  }, []);

  if (!rows || rows.length === 0) return null;

  async function leave(row: OutsideReading) {
    setNote("");
    try {
      await outsideLeave(row.id);
      setRows((current) => (current || []).filter((item) => item.id !== row.id));
      setOpen((current) => (current?.id === row.id ? null : current));
    } catch (reason: unknown) {
      setNote(reason instanceof Error ? reason.message : "Retrait impossible");
    }
  }

  return (
    <div className="mt-3 px-2">
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Reçues</p>
      <ul className="mt-1 space-y-1">
        {rows.map((row) => {
          const from = row.society ? `${row.owner_name} · ${row.society}` : row.owner_name;
          return (
            <li key={row.id}>
              <button type="button" className="w-full rounded-xl px-2 py-1.5 text-left hover:bg-black/5" onClick={() => { setNote(""); setOpen(row); }}>
                <span className="block truncate text-sm font-medium text-slate-700">{row.title}</span>
                <span className="block truncate text-[11px] font-medium text-violet-700">{row.kind === "week" ? "Tâche" : "Fiche"} · {from}</span>
              </button>
            </li>
          );
        })}
      </ul>
      {note && !open ? <p className="mt-1 text-xs text-rose-700">{note}</p> : null}
      {open ? (
        <Modal onClose={() => setOpen(null)} sheetClass="crm-pop w-full max-w-md rounded-2xl border border-slate-200 p-6 shadow-[0_30px_80px_-36px_rgba(15,23,42,0.45)]">
          {() => {
            const from = open.society ? `${open.owner_name} · ${open.society}` : open.owner_name;
            return (
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-violet-700">{open.kind === "week" ? "Tâche" : "Fiche"} · {from}</p>
                <h2 className="mt-1 text-lg font-semibold text-slate-800">{open.title}</h2>
                <p className="mt-1 text-xs text-slate-500">Aperçu. Rien n’est copié dans ton CRM.</p>
                <dl className="mt-3 space-y-2">
                  {open.properties.map((prop, index) => (
                    <div key={`${prop.label}-${index}`}>
                      <dt className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">{prop.label}</dt>
                      <dd className="text-sm text-slate-700">{prop.value}</dd>
                    </div>
                  ))}
                </dl>
                {note ? <p className="mt-3 text-xs text-rose-700">{note}</p> : null}
                <button type="button" className="mt-4 rounded-full border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-600" onClick={() => void leave(open)}>Retirer cette lecture</button>
              </div>
            );
          }}
        </Modal>
      ) : null}
    </div>
  );
}
